import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { pettyCashAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle, XCircle, AlertTriangle, Loader2, DollarSign, Wallet,
  Calendar, User, FileText, Clock, AlertCircle
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

export default function PettyCashActionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [tokenData, setTokenData] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [actionCompleted, setActionCompleted] = useState(false);
  const [resultStatus, setResultStatus] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('No token provided');
      setLoading(false);
      return;
    }
    
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await pettyCashAPI.verifyEmailAction(token);
      setTokenData(res.data);
      
      if (res.data.already_processed) {
        setError(`This ${res.data.item_type} has already been processed (Status: ${res.data.item.status})`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async () => {
    setSubmitting(true);
    try {
      const res = await pettyCashAPI.executeEmailAction(token, remarks);
      setActionCompleted(true);
      setResultStatus(res.data.status);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Verifying your request...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-bold text-red-800 mb-2">Link Invalid or Expired</h2>
            <p className="text-center text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Go to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (actionCompleted) {
    const isApproved = resultStatus === 'approved' || resultStatus === 'disbursed';
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 ${isApproved ? 'bg-gradient-to-br from-green-50 to-emerald-100' : 'bg-gradient-to-br from-red-50 to-rose-100'}`}>
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className={`h-20 w-20 rounded-full flex items-center justify-center mb-4 ${isApproved ? 'bg-green-100' : 'bg-red-100'}`}>
              {isApproved ? (
                <CheckCircle className="h-10 w-10 text-green-600" />
              ) : (
                <XCircle className="h-10 w-10 text-red-600" />
              )}
            </div>
            <h2 className={`text-2xl font-bold mb-2 ${isApproved ? 'text-green-800' : 'text-red-800'}`}>
              {isApproved ? 'Approved!' : 'Rejected'}
            </h2>
            <p className="text-center text-muted-foreground mb-6">
              {tokenData?.item_type === 'expense' 
                ? `The expense of ${formatCurrency(tokenData?.item?.amount)} has been ${resultStatus}.`
                : `The cash request of ${formatCurrency(tokenData?.item?.amount)} has been ${resultStatus}.`
              }
            </p>
            {remarks && (
              <div className="w-full p-3 bg-muted rounded-lg mb-4">
                <p className="text-sm text-muted-foreground">Your remarks: "{remarks}"</p>
              </div>
            )}
            <p className="text-sm text-muted-foreground">You can close this window now.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { item_type, action, item } = tokenData || {};
  const isApprove = action === 'approve';
  const isExpense = item_type === 'expense';

  return (
    <div className={`min-h-screen flex items-center justify-center p-4 ${isApprove ? 'bg-gradient-to-br from-green-50 to-emerald-100' : 'bg-gradient-to-br from-red-50 to-rose-100'}`}>
      <Card className="w-full max-w-lg shadow-xl">
        <CardHeader className={`rounded-t-lg ${isApprove ? 'bg-green-600' : 'bg-red-600'} text-white`}>
          <div className="flex items-center gap-3">
            {isApprove ? (
              <CheckCircle className="h-8 w-8" />
            ) : (
              <XCircle className="h-8 w-8" />
            )}
            <div>
              <CardTitle className="text-white">
                {isApprove ? 'Approve' : 'Reject'} {isExpense ? 'Expense' : 'Cash Request'}
              </CardTitle>
              <CardDescription className="text-white/80">
                No login required
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* Amount Banner */}
          <div className={`p-4 rounded-lg text-center ${isApprove ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <p className="text-sm text-muted-foreground mb-1">Amount</p>
            <p className={`text-3xl font-bold ${isApprove ? 'text-green-700' : 'text-red-700'}`}>
              {formatCurrency(item?.amount)}
            </p>
          </div>

          {/* Item Details */}
          <div className="space-y-3">
            {isExpense ? (
              <>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <span className="text-2xl">{item?.category_icon || '📋'}</span>
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{item?.category_name || 'Unknown'}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Description</p>
                    <p className="font-medium">{item?.description}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Date</p>
                      <p className="font-medium">{formatDate(item?.expense_date)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <DollarSign className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Payment</p>
                      <p className="font-medium text-sm">{item?.payment_mode_name}</p>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Submitted By</p>
                    <p className="font-medium">{item?.created_by_name}</p>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                  <Wallet className="h-6 w-6 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Purpose</p>
                    <p className="font-medium">{item?.purpose}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Expected Use</p>
                      <p className="font-medium">{formatDate(item?.expected_usage_date)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Requested By</p>
                      <p className="font-medium text-sm">{item?.requested_by_name}</p>
                    </div>
                  </div>
                </div>
                
                {item?.notes && (
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Notes</p>
                      <p className="font-medium">{item?.notes}</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Remarks Input */}
          <div className="space-y-2">
            <Label htmlFor="remarks">Remarks (Optional)</Label>
            <Textarea
              id="remarks"
              placeholder={isApprove ? "Add any approval notes..." : "Reason for rejection..."}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              data-testid="email-action-remarks"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              className={`flex-1 ${isApprove ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
              onClick={handleAction}
              disabled={submitting}
              data-testid="confirm-email-action-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : isApprove ? (
                <CheckCircle className="h-4 w-4 mr-2" />
              ) : (
                <XCircle className="h-4 w-4 mr-2" />
              )}
              {isApprove ? 'Confirm Approval' : 'Confirm Rejection'}
            </Button>
          </div>

          {/* Info Note */}
          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-blue-800">
              This action is secure and doesn't require login. The link expires in 7 days.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
